import { Fragment, useEffect, useRef, useState } from 'react';
import { ButtonDropdown, ButtonV2 } from '@cimplico/components';
import { ClientQueryReply } from '../../types/client-query';
import { InternalCommentReply } from '../../types/internal-comment';
import clsx from 'clsx';
import Author from './Author';
import Editor from '../rich-text-editor/Editor';

export type UpdateReplyProps = {
    id: string;
    rawContent: string;
    structuredContent?: { [key: string]: unknown };
};

type ReplyProps = {
    /**
     * Reply content
     */
    reply: InternalCommentReply | ClientQueryReply;
    /**
     * Whether reply is currently being edited
     */
    isEditing: boolean;
    /**
     * Whether edit reply query is loading
     */
    isSaving: boolean;
    /**
     * Whether reply can be edited
     */
    canEdit: boolean;
    /**
     * Additonal actions to show in button dropdown
     */
    extraActions?: { label: string; onClick: (params: UpdateReplyProps) => void }[];
    /**
     * Callback when reply is currently being edited
     */
    onEditing: (isEditing: boolean) => void;
    /**
     * Callback when reply is being deleted
     */
    onDelete: (id: string) => void;
    /**
     * Callback when reply is being updated
     */
    onUpdate: (params: UpdateReplyProps) => void;
};

/**
 * Individual reply section rendered within the replies list
 */
function Reply({ reply, isEditing, isSaving, canEdit, extraActions, onEditing, onUpdate, onDelete }: ReplyProps): JSX.Element {
    const editorRef = useRef<HTMLDivElement>(null);
    const editorContainerRef = useRef<HTMLDivElement>(null);

    const [showingMore, setShowingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [editingText, setEditingText] = useState({ rawContent: reply.rawReply, structuredContent: reply.structuredReply });

    useEffect(() => {
        if (!editorRef.current || !editorContainerRef.current) {
            setHasMore(false);
        } else {
            setHasMore(editorRef.current.clientHeight > editorContainerRef.current.clientHeight);
        }
    }, [reply]);

    return (
        <Fragment key={reply.id}>
            <li className="w-full" key={reply.id}>
                <div>
                    <Author createdBy={reply.createdBy} createdAt={reply.createdAt} />
                    <div
                        ref={editorContainerRef}
                        className={clsx(
                            !isEditing && 'overflow-hidden',
                            showingMore || isEditing ? 'h-fit min-h-[150px]' : 'max-h-[150px]',
                            'flex flex-col mt-1'
                        )}
                    >
                        {/* REPLY CONTENT */}
                        <div className={clsx('h-full w-full basis-0 grow')} ref={editorRef}>
                            <Editor
                                readOnly={!isEditing}
                                defaultContent={reply.structuredReply ? JSON.stringify(reply.structuredReply) : undefined}
                                onContentChange={(raw, structured): void => {
                                    setEditingText({ rawContent: raw, structuredContent: JSON.parse(structured) });
                                }}
                            />
                        </div>
                    </div>
                    {hasMore && !isEditing && (
                        <button
                            className="ml-2 text-gray-800 font-medium text-xs mb-2"
                            onClick={(): void => {
                                setShowingMore(!showingMore);
                            }}
                        >
                            {showingMore ? 'Show less' : 'Show more'}
                        </button>
                    )}
                    {canEdit ? (
                        isEditing ? (
                            <div className="flex items-center justify-end gap-2 mt-1">
                                <ButtonV2
                                    size="xs"
                                    label="Cancel"
                                    type="hollow"
                                    onClick={(): void => onEditing(false)}
                                    isDisabled={isSaving}
                                />
                                {extraActions && extraActions.length > 0 ? (
                                    <ButtonDropdown
                                        size="xs"
                                        label="Save"
                                        variant="primary"
                                        disabled={isSaving || !editingText.rawContent}
                                        isLoading={isSaving}
                                        onClick={(): void =>
                                            onUpdate({
                                                id: reply.id,
                                                rawContent: editingText.rawContent,
                                                structuredContent: editingText.structuredContent,
                                            })
                                        }
                                    >
                                        {extraActions.map((action, idx) => (
                                            <ButtonDropdown.SecondaryAction
                                                key={idx}
                                                label={action.label}
                                                disabled={isSaving || !editingText.rawContent}
                                                onClick={(): void =>
                                                    action.onClick({
                                                        id: reply.id,
                                                        rawContent: editingText.rawContent,
                                                        structuredContent: editingText.structuredContent,
                                                    })
                                                }
                                            />
                                        ))}
                                    </ButtonDropdown>
                                ) : (
                                    <ButtonV2
                                        size="xs"
                                        label="Save"
                                        type="primary"
                                        onClick={(): void =>
                                            onUpdate({
                                                id: reply.id,
                                                rawContent: editingText.rawContent,
                                                structuredContent: editingText.structuredContent,
                                            })
                                        }
                                        isLoading={isSaving}
                                        isDisabled={isSaving || !editingText.rawContent}
                                    />
                                )}
                            </div>
                        ) : (
                            <div className="flex items-center gap-2">
                                <ButtonV2
                                    size="xs"
                                    label="Edit"
                                    type="hollow"
                                    isDisabled={isSaving}
                                    onClick={(): void => onEditing(true)}
                                />
                                <ButtonV2
                                    size="xs"
                                    label="Delete"
                                    type="hollow"
                                    isDisabled={isSaving}
                                    isLoading={isSaving}
                                    onClick={(): void => onDelete(reply.id)}
                                />
                            </div>
                        )
                    ) : (
                        <></>
                    )}
                </div>
            </li>
            <div className="h-[1px] bg-gray-200 w-full" />
        </Fragment>
    );
}

export default Reply;
